
import { useNavigate } from "react-router-dom";
import { StepIndicator } from "@/components/onboarding/StepIndicator";
import { MultipleChoiceStep } from "@/components/onboarding/MultipleChoiceStep";
import { ScaleStep } from "@/components/onboarding/ScaleStep";
import { GoalsStep } from "@/components/onboarding/GoalsStep";
import { MultiSelectStep } from "@/components/onboarding/MultiSelectStep";
import { StepNavigation } from "@/components/onboarding/StepNavigation";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useOnboardingSteps } from "@/hooks/use-onboarding-steps";
import { ArtistQuestion } from "@/components/onboarding/ArtistQuestion";

const Onboarding = () => {
  const navigate = useNavigate();
  const {
    formData,
    setFormData,
    loading,
    handleSubmit,
  } = useOnboarding();

  const {
    step,
    totalSteps,
    nextStep,
    previousStep,
    isFirstStep,
    isLastStep,
  } = useOnboardingSteps(7); // Increased to 7 steps to include artist question

  const handleStepChange = (field: string, value: any, processNextStep = true) => {
    setFormData({ ...formData, [field]: value });
    // Only auto-advance for non-slider steps
    if (step < totalSteps && field !== "connectionPreference" && processNextStep) {
      nextStep();
    }
  };

  const handleComplete = async () => {
    await handleSubmit();
    navigate("/events");
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <MultipleChoiceStep
            title="Do you consider yourself more of a..."
            choices={[
              { id: "blockbuster", label: "Blockbuster museum lover" },
              { id: "hidden_gem", label: "Hidden gem enthusiast" },
            ]}
            selected={formData.museumPreference}
            onChange={(value) => handleStepChange("museumPreference", value)}
          />
        );
      case 2:
        return (
          <ScaleStep
            title="I am an Introvert person"
            value={formData.introvertScale?.toString() || ""}
            onChange={(value) =>
              handleStepChange("introvertScale", parseInt(value))
            }
          />
        );
      case 3:
        return (
          <MultipleChoiceStep
            title="What best describes an ideal Saturday afternoon for you?"
            choices={[
              { id: "planned", label: "Well-planned in advance" },
              { id: "spontaneous", label: "Spontaneous" },
            ]}
            selected={formData.planningStyle}
            onChange={(value) => handleStepChange("planningStyle", value)}
          />
        );
      case 4:
        return (
          <ScaleStep
            title="I am a creative person"
            value={formData.creativityScale?.toString() || ""}
            onChange={(value) =>
              handleStepChange("creativityScale", parseInt(value))
            }
          />
        );
      case 5:
        return (
          <GoalsStep
            value={formData.connectionPreference}
            onChange={(value) =>
              handleStepChange("connectionPreference", value)
            }
          />
        );
      case 6:
        return (
          <MultiSelectStep
            title="Select all art experiences that you might be interested in:"
            choices={[
              { id: "blockbuster", label: "Blockbuster museum exhibition" },
              { id: "hidden_gem", label: "Hidden gem galleries stroll" },
              { id: "scavenger", label: "Museum scavenger hunt" },
              { id: "studio", label: "Artist studio visit" },
              { id: "workshop", label: "Creative workshop" },
            ]}
            selected={formData.selectedExperiences}
            onChange={(values) =>
              handleStepChange("selectedExperiences", values, false)
            }
          />
        );
      case 7:
        return (
          <ArtistQuestion
            value={formData.memberType || "member"}
            onChange={(value) => handleStepChange("memberType", value)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-primary-light p-4">
      <div className="max-w-md mx-auto space-y-8">
        <StepIndicator currentStep={step} totalSteps={totalSteps} />
        {renderStep()}
        <StepNavigation
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          onBack={previousStep}
          onNext={nextStep}
          onComplete={handleComplete}
          loading={loading}
          showNext={step === 5 || step === 6}
        />
      </div>
    </div>
  );
};

export default Onboarding;
